import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, Shield, Users, Home, FileText, Globe, ArrowRight, CheckCircle, Calendar, Award, ChevronLeft } from 'lucide-react';
import { practiceAreas } from '../data';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Building2, Shield, Users, Home, FileText, Globe,
};

export default function PracticeAreaDetailPage() {
  const { slug } = useParams();
  const area = practiceAreas.find(a => a.slug === slug);

  if (!area) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-[#e8e4dc] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Practice Area Not Found</h1>
          <Link to="/practice-areas" className="text-[#c9a84c] hover:text-[#e0cb82] transition-colors">
            ← Back to Practice Areas
          </Link>
        </div>
      </div>
    );
  }

  const Icon = iconMap[area.icon] || Building2;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
      {/* Hero */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#050d1a] via-[#0a1628] to-[#0f2240]" />
        <div className="absolute top-20 right-20 w-[500px] h-[500px] bg-[#c9a84c]/5 rounded-full blur-[150px]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <Link to="/practice-areas" className="inline-flex items-center gap-2 text-sm text-[#c9a84c]/70 hover:text-[#c9a84c] mb-8 transition-colors">
            <ChevronLeft className="w-4 h-4" /> Back to Practice Areas
          </Link>
          <div className="flex items-center gap-5 mb-6">
            <div className="w-16 h-16 rounded-xl bg-[#c9a84c]/10 flex items-center justify-center">
              <Icon className="w-8 h-8 text-[#c9a84c]" />
            </div>
            <div>
              <div className="gold-line mb-3" />
              <h1 className="text-4xl md:text-5xl font-bold text-[#e8e4dc]" style={{ fontFamily: 'Playfair Display, serif' }}>
                {area.title}
              </h1>
            </div>
          </div>
          <p className="text-lg text-[#e8e4dc]/60 max-w-3xl leading-relaxed">
            {area.longDescription}
          </p>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <div className="gold-line mb-6" />
            <h2 className="text-3xl font-bold text-[#e8e4dc]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Featured <span className="text-[#c9a84c]">Case Studies</span>
            </h2>
          </div>
          <div className="space-y-8">
            {area.caseStudies.map((cs, idx) => (
              <motion.div
                key={cs.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.15 }}
                className="p-8 md:p-10 rounded-2xl bg-gradient-to-b from-[#0f2240]/50 to-[#0a1628]/50 border border-[#c9a84c]/10 hover:border-[#c9a84c]/20 transition-all duration-500"
              >
                <div className="flex flex-col md:flex-row gap-8">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <span className="px-3 py-1 rounded-full bg-[#c9a84c]/10 text-xs font-semibold text-[#c9a84c] uppercase tracking-wider">
                        {cs.category}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-[#e8e4dc]/40">
                        <Calendar className="w-3 h-3" /> {cs.year}
                      </span>
                    </div>
                    <h3 className="text-2xl font-bold text-[#e8e4dc] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {cs.title}
                    </h3>
                    <p className="text-sm text-[#e8e4dc]/40 mb-4">Client: {cs.client}</p>
                    <p className="text-[#e8e4dc]/60 leading-relaxed mb-6">{cs.summary}</p>
                    <div className="p-4 rounded-xl bg-[#0a1628]/60 border border-[#c9a84c]/10">
                      <p className="text-xs text-[#c9a84c] font-semibold uppercase tracking-wider mb-2">Outcome</p>
                      <p className="text-sm text-[#e8e4dc]/70">{cs.outcome}</p>
                    </div>
                  </div>
                  <div className="md:w-56 shrink-0 flex flex-col items-center justify-center p-6 rounded-xl bg-[#c9a84c]/5 border border-[#c9a84c]/10">
                    <Award className="w-8 h-8 text-[#c9a84c] mb-3" />
                    <div className="text-lg font-bold text-[#c9a84c] text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {cs.result}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Other Practice Areas */}
      <section className="py-20 border-t border-[#c9a84c]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-bold text-[#e8e4dc] mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
            Other Practice Areas
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {practiceAreas.filter(a => a.slug !== slug).map((other) => {
              const OtherIcon = iconMap[other.icon] || Building2;
              return (
                <Link
                  key={other.id}
                  to={`/practice-areas/${other.slug}`}
                  className="group flex items-center gap-4 p-5 rounded-xl bg-[#0f2240]/30 border border-[#c9a84c]/10 hover:border-[#c9a84c]/20 hover:bg-[#0f2240]/50 transition-all"
                >
                  <div className="w-10 h-10 rounded-lg bg-[#c9a84c]/10 flex items-center justify-center shrink-0 group-hover:bg-[#c9a84c]/20 transition-colors">
                    <OtherIcon className="w-5 h-5 text-[#c9a84c]" />
                  </div>
                  <span className="text-sm font-medium text-[#e8e4dc] group-hover:text-[#c9a84c] transition-colors">{other.title}</span>
                  <ArrowRight className="w-4 h-4 text-[#c9a84c]/40 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="p-12 rounded-2xl bg-gradient-to-b from-[#0f2240]/60 to-[#0a1628]/60 border border-[#c9a84c]/15 gold-glow">
            <h2 className="text-3xl font-bold text-[#e8e4dc] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Need Help with <span className="text-[#c9a84c]">{area.title}?</span>
            </h2>
            <p className="text-[#e8e4dc]/60 mb-8 max-w-lg mx-auto">
              Speak with one of our experienced {area.title.toLowerCase()} attorneys. Initial consultations are available at a reduced rate.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/consultation"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#c9a84c] to-[#b08e2e] text-[#0a1628] font-semibold rounded-xl hover:shadow-xl hover:shadow-[#c9a84c]/20 transition-all duration-300 hover:scale-105"
              >
                Book Consultation <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="tel:+12017984100"
                className="inline-flex items-center gap-2 px-8 py-4 border border-[#c9a84c]/30 text-[#c9a84c] font-medium rounded-xl hover:bg-[#c9a84c]/10 transition-all duration-300"
              >
                Call (201) 798-4100
              </a>
            </div>
          </div>
        </div>
      </section>
    </motion.div>
  );
}
